import { CheckboxsetWebElementBase } from './CheckboxsetWebElementBase';
import { By, error, Key, WebElement } from 'selenium-webdriver';

type LabelInputs = {
  label: WebElement;
  input: WebElement;
};

/**
 * The component WebElement for [oj-c-checkboxset](../../jsdocs/oj-c.Checkboxset.html).
 * Do not instantiate this class directly, instead, use
 * [findCheckboxset](../functions/findCheckboxset.html).
 */
export class CheckboxsetWebElement extends CheckboxsetWebElementBase {
  /**
   * Sets the value of "value" property for the Checkboxset component
   * @param value The value to set for "value"
   * @throws {ElementNotInteractableError} if the API is called when the control is readonly or disabled
   */
  public async changeValue(value: Array<string | number> | null): Promise<void> {
    // Check if the control is interactable
    if (!(await this.isInteractable())) {
      // Cannot interact when the control is disabled or readonly
      throw new error.ElementNotInteractableError(
        'oj-c-checkboxset: Value cannot be changed when the control is disabled or readonly'
      );
    }

    await this.whenBusyContextReady();
    await this.toggleValues(value);
  }

  /**
   * Checks if the Checkboxset is in interactable state
   */
  private async isInteractable(): Promise<boolean> {
    const isReadonly = await this.getReadonly();
    const isDisabled = await this.getDisabled();
    return !(isReadonly || isDisabled);
  }

  private async getInputsWithLabels() {
    const labelsAndInputs: Array<LabelInputs> = [];
    const labels = await this.findElements(By.css('label'));

    const promises = labels.map(async (label) => {
      const inputs = await label.findElements(By.css('input'));

      // make sure the label is associated with an input
      if (inputs.length > 0) {
        labelsAndInputs.push({
          label,
          input: inputs[0]
        });
      }
    });

    await Promise.all(promises);
    return labelsAndInputs;
  }

  /**
   * Check checkboxes that have value within values array
   */
  private async toggleValues(values: Array<string | number> | null) {
    const inputs = await this.getInputsWithLabels();

    const promises = inputs.map(async ({ label, input }) => {
      const value = await input.getAttribute('value');
      const isSelected = await input.isSelected();
      const isInValues = values?.includes(value);
      if ((isInValues && !isSelected) || (!isInValues && isSelected)) {
        // toggle checkbox
        await label.sendKeys(Key.SPACE);
      }
    });

    await Promise.all(promises);
  }
}
